import { Action } from 'redux';
import { Metadata } from '../../types/Metadata';
import { View } from '../../types/View';
import { Info } from '../../types/Info';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { excludeEmptyFilters } from '../../utils';
import { EFSCompanyDirectionId } from '../../enums/EFSCompanyDirectionId';
import { EFSExperienceId } from '../../enums/EFSExperienceId';
import { ViewCategory } from '../../enums/viewCategory';

export interface ViewState {
  selected?: View.Setting;
  groups?: View.Group[];
  swimlanes?: View.Group[];
  cards?: { [x: string]: Metadata };
  newCards?: Metadata[];
  tempFilters?: View.FiltersField[];
  filters?: View.FiltersField[];
  sortBy?: View.SortByField[];
  data?: Metadata[];
  groupData?: { [x: string]: Metadata[] };
  pagination?: View.Pagination;
  info?: Info;
  searchHash?: string;
  reqTimeInMs?: number; // prevent race condition
  backingUpFor?: View.BackingUpFor;
  viewingAs?: Metadata;
  actingAs?: Metadata;
  isCheckedAll?: boolean;
  toggledRefIds?: string[];
  editMode?: boolean;
  invalidCells?: { refId: string; field: string; serverSide?: boolean }[];
  selectedEFSPersona?: {
    efsCompanyDirectionId: number;
    efsExperienceId: number;
  };
  hasUnsavedChanges?: boolean;
  efsItemData?: {
    [itemId: number]: {
      efsActionName?: string;
      efsPersonas?: {
        efsDisplayOrder;
        efsExperienceId;
        efsCompanyDirectionId;
        efsTimeFrame;
      }[];
    };
  };
}

export const SET_UNSAVED_CHANGES = 'SET_UNSAVED_CHANGES';
export const UPDATE_VIEW_STATE = 'UPDATE_VIEW_STATE';
export const UPDATE_VIEW_STATE_REQ_TIME = 'UPDATE_VIEW_STATE_REQ_TIME';
//export const UPDATE_VIEW_STATE_DATA = 'UPDATE_VIEW_STATE_DATA';
const UPDATE_SELECTED_VIEW_STATE = 'UPDATE_SELECTED_VIEW_STATE';
const UPDATE_GROUP_VIEW_STATE = 'UPDATE_GROUP_VIEW_STATE';
const UPDATE_DATA_FIELD = 'UPDATE_DATA_FIELD';
const UPDATE_SWIMLANE_DATA = 'UPDATE_SWIMLANE_DATA';
const TOGGLE_SWIMLANE = 'TOGGLE_SWIMLANE';
const TOGGLE_VIEW_CHECKBOX = 'TOGGLE_VIEW_CHECKBOX';
const RESET_VIEW_CHECKBOX = 'RESET_VIEW_CHECKBOX';
export const UPDATE_INVALID_CELLS = 'UPDATE_INVALID_CELLS';
const SET_TEMP_FILTERS = 'SET_TEMP_FILTERS';

export const setUnsavedChanges = (hasUnsavedChanges: boolean) => ({
  type: SET_UNSAVED_CHANGES,
  hasUnsavedChanges
});

export const setTempFilters = (tempFilters?: View.FiltersField[]) => ({
  type: SET_TEMP_FILTERS,
  tempFilters
});

export const resetViewCheckbox = () => ({
  type: RESET_VIEW_CHECKBOX
});

export const toggleViewCheckbox = (refId?: string) => ({
  type: TOGGLE_VIEW_CHECKBOX,
  refId
});

type SwimlaneData = {
  groupId?: string;
  swimlaneId: string;
  swimlanes?: View.Group[];
  cards?: Metadata[];
  expanded?: boolean;
};

type TabularData = {
  refId: string;
  item: Metadata;
};

export const updateDataField = (refId: string, item: Metadata) => ({
  type: UPDATE_DATA_FIELD,
  tabularData: { refId, item }
});

export const toggleSwimlane = (swimlaneId: string, expanded: boolean) => ({
  type: TOGGLE_SWIMLANE,
  swimlaneData: { swimlaneId, expanded }
});
export const updateSwimlaneData = (swimlaneData: SwimlaneData) => ({
  type: UPDATE_SWIMLANE_DATA,
  swimlaneData
});

export const updateSelectedView = (selected: View.Setting) => ({
  type: UPDATE_SELECTED_VIEW_STATE,
  selected
});
export const updateViewStateReqTime = (reqTimeInMs: number) => ({
  type: UPDATE_VIEW_STATE_REQ_TIME,
  reqTimeInMs
});
export const updateViewState = (
  view: View.Setting,
  newState: Omit<Partial<ViewState>, 'selected'>
) => ({
  type: UPDATE_VIEW_STATE,
  viewId: view.props?.refId,
  ...newState
});
export const updateViewGroup = (
  view: View.Setting,
  group: View.Group | View.Group[],
  data: Metadata[]
) => ({
  type: UPDATE_GROUP_VIEW_STATE,
  viewId: view.props.refId,
  group,
  data
});

export const updateInvalidCells = (
  invalidCells: { refId: string; field: string; serverSide?: boolean }[]
) => ({
  type: UPDATE_INVALID_CELLS,
  invalidCells
});

type ViewAction = Action &
  Partial<ViewState> & { group: View.Group } & {
    updatedGroups: View.Group[];
  } & {
    viewId: string;
    refId: string;
    swimlaneData: SwimlaneData;
    tabularData: TabularData;
    invalidCell: { refId: string; field: string };
  };

const ViewReducer = (
  state: ViewState = { cards: {}, hasUnsavedChanges: false, invalidCells: [] },
  action: ViewAction
): ViewState => {
  const {
    type,
    group,
    updatedGroups,
    viewId,
    refId,
    newCards,
    swimlaneData,
    hasUnsavedChanges,
    tabularData,
    invalidCell,
    ...newState
  } = action;
  switch (type) {
    case UPDATE_DATA_FIELD:
      state?.data?.map(d => {
        if (d?.props?.refId == tabularData?.refId) tabularData?.item;
      });
      return state;
    case SET_TEMP_FILTERS:
      return { ...state, tempFilters: action.tempFilters };
    case RESET_VIEW_CHECKBOX:
      return { ...state, isCheckedAll: null, toggledRefIds: null };
    case TOGGLE_VIEW_CHECKBOX:
      const isCheckedAll = state.isCheckedAll;
      let toggledRefIds = state.toggledRefIds || [];
      if (refId) {
        if (toggledRefIds.includes(refId)) {
          toggledRefIds = toggledRefIds.filter(id => id != refId);
        } else {
          toggledRefIds = toggledRefIds.concat(refId);
        }
        return { ...state, toggledRefIds };
      } else {
        return { ...state, isCheckedAll: !isCheckedAll, toggledRefIds: null };
      }
    case SET_UNSAVED_CHANGES:
      return { ...state, hasUnsavedChanges };
    case TOGGLE_SWIMLANE:
      if (swimlaneData) {
        const { swimlaneId, expanded } = swimlaneData;
        if (!swimlaneId) {
          state.swimlanes?.forEach(s => {
            if (s.expanded != expanded) s.expanded = expanded;
          });
          return { ...state, swimlanes: [...state.swimlanes] };
        }
        const swimlane = findOne(state.swimlanes, { id: swimlaneId });
        if (swimlane) {
          swimlane.expanded = expanded;
          return { ...state, swimlanes: [...state.swimlanes] };
        }
      }
      return state;
    case UPDATE_SWIMLANE_DATA:
      if (swimlaneData) {
        const { groupId, swimlaneId, swimlanes: _swimlanes } = swimlaneData;
        const swimlane = findOne(swimlaneData.swimlanes, { id: swimlaneId });
        const curSwimlane = findOne(state.swimlanes, { id: swimlaneId });
        if (!swimlane || !curSwimlane) return state;

        const swimlanes = state.swimlanes?.map(sl => {
          if (sl.id == swimlaneId) {
            if (!groupId) {
              sl.count = swimlane.count;
              sl.subgroups = state.groups.map(g => {
                const s = findOne(swimlane.subgroups, { groupId: g.id });
                if (!s) {
                  return {
                    groupId: g.id,
                    cardRefIds: [],
                    count: 0
                  };
                } else {
                  return s;
                }
              });
            } else {
              const subgroup = findOne(swimlane.subgroups, { groupId });
              const curSubgroup = findOne(sl.subgroups, { groupId });
              if (subgroup && curSubgroup) {
                curSubgroup.cardRefIds = subgroup.cardRefIds;
                curSubgroup.count = subgroup.count;
              }
            }
          }
          return sl;
        });
        const cards = swimlaneData.cards?.reduce((map, card) => {
          map[card.props.refId] = card;
          return map;
        }, state.cards || {});
        return { ...state, cards, swimlanes };
      }
      return state;
    case UPDATE_SELECTED_VIEW_STATE:
      if (newState?.selected?.id) {
        if (!newState?.selected?.category) {
          newState.selected.category = ViewCategory.Views;
        }
      }
      return {
        selected: null,
        data: [],
        filters: [],
        sortBy: [],
        groups: [],
        swimlanes: [],
        cards: {},
        reqTimeInMs: state.reqTimeInMs || 0,
        efsItemData: null,
        tempFilters: null,
        invalidCells: [],
        selectedEFSPersona: {
          efsCompanyDirectionId: EFSCompanyDirectionId.MaintainDirection,
          efsExperienceId: EFSExperienceId.FirstTime
        },
        ...newState
      };
    case UPDATE_VIEW_STATE_REQ_TIME:
      return {
        ...state,
        reqTimeInMs: newState.reqTimeInMs
      };
    case UPDATE_VIEW_STATE:
      if (viewId != state.selected?.props?.refId) return state;
      if (newState.reqTimeInMs && newState.reqTimeInMs < state.reqTimeInMs)
        return state;
      if (newState.groups) {
        newState.groups = removeDuplicates(newState.groups);
      }
      if (newCards) {
        const cards = state.cards || {};
        newCards.forEach(c => {
          cards[c.props.refId] = c;
        });
        newState.cards = cards;
      }
      if (
        newState.filters &&
        !deepEquals(
          excludeEmptyFilters(state.filters),
          excludeEmptyFilters(newState.filters)
        )
      ) {
        newState.isCheckedAll = null;
        newState.toggledRefIds = null;
      }
      return { ...state, ...newState };
    case UPDATE_GROUP_VIEW_STATE:
      if (viewId != state.selected?.props?.refId) return state;
      if (state.groups && group) {
        const cards = state.cards;
        let updated = false;
        const groups = state.groups.map(g => {
          const _group = Array.isArray(group)
            ? findOne(group, { id: g.id })
            : group;
          if (_group?.id != g.id) return g;
          if (g.reqTimeInMs && _group.reqTimeInMs < g.reqTimeInMs) return g;
          newState.data?.map(d => {
            if (d?.props?.refId) cards[d.props.refId] = d;
          });
          updated = true;
          return {
            ...g,
            ..._group
          };
        });
        if (updated) return { ...state, groups, cards };
      }
      return state;
    case UPDATE_INVALID_CELLS:
      return {
        ...state,
        invalidCells: action.invalidCells || []
      };
    default:
      return state;
  }
};

const removeDuplicates = (groups: View.Group[]) => {
  if (!groups) return null;
  const ids = groups.map(g => g.id);
  return groups.filter((g, i) => ids.indexOf(g.id) == i);
};

export default ViewReducer;
